import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ApiScanVideoProgress,
  buildScanVideoPollConnectionLostProgress,
  computeScanVideoContentHashStreaming,
  createScanVideoConcurrencyLimiter,
  forgetScanVideoOperationId,
  getMissingScanVideoPartNumbers,
  getScanVideoMultipartUploadPct,
  getScanVideoPartCount,
  isScanVideoAnalysisOperation,
  kickOffVideoGroupImageCaptureJob,
  partsToComplete,
  rememberScanVideoOperationId,
  resolveVideoContentType,
  SCAN_VIDEO_JOB_POLL_INTERVAL_MS,
  SCAN_VIDEO_JOB_POLL_MAX_CONSECUTIVE_FAILURES,
  SCAN_VIDEO_MULTIPART_PART_SIZE_BYTES,
  SCAN_VIDEO_PART_CONCURRENCY,
  SCAN_VIDEO_PART_MAX_ATTEMPTS,
  SCAN_VIDEO_S3_PUT_RETRY_DELAY_MS,
  type ScanVideoConcurrencyLimiter,
  type ScanVideoUploadedPart,
  sliceScanVideoPart,
  toApiScanVideoProgressFromOperation,
} from '@kaiizen/kai-pro-api';
import type { AIScanVideoActiveWorkSnapshotItem } from './useAIScanVideoActiveWork';

import { logError } from '@/Client/Lib/logger';
import { useAuth, useKaiProApiClient } from '@/Client/State';

type S3UploadError = Error & { httpStatus?: number; isS3UploadError: true };

const makeS3UploadError = (message: string, httpStatus?: number): S3UploadError => {
  const error = new Error(message) as S3UploadError;
  error.isS3UploadError = true;
  if (httpStatus != null) error.httpStatus = httpStatus;
  return error;
};

const isS3UploadError = (error: unknown): error is S3UploadError => error instanceof Error && (error as S3UploadError).isS3UploadError === true;

export type BatchPhase = 'idle' | 'submitting' | 'processing';

export type UploadFileItem = {
  fileId: string;
  file: File;
  durationSeconds?: number | null;
  width?: number | null;
  height?: number | null;
  videoRecordedAt?: Date | null;
};

export type BatchUploadHandlers = {
  onFileUploadProgress?: (fileId: string, pct: number) => void;
  onFileUploadFailed?: (fileId: string, message: string) => void;
  onFileUploadComplete?: (fileId: string, videoId: string) => void;
  /** Fired when analysis kickoff starts for an uploaded video. */
  onAnalyzeStarted?: (fileId: string, videoId: string) => void;
  onJobProgress?: (fileId: string, polled: ApiScanVideoProgress) => void;
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const SCAN_VIDEO_MULTIPART_UPLOAD_EVENT = 'aiScanVideoS3MultipartUpload';

/** True for the S3 responses that mean the presigned part URL is stale and must be re-signed. */
const isExpiredPresignError = (error: unknown): boolean => isS3UploadError(error) && (error.httpStatus === 403 || error.httpStatus === 400);

/**
 * PUT one part slice to its presigned URL, resolving with the part's S3 ETag (from the response
 * header). Resolves with '' when the ETag header is not exposed by the bucket CORS policy — the
 * caller then completes with an empty parts list so the server reconciles from S3 ListParts.
 */
const putPartWithProgress = (
  url: string,
  blob: Blob,
  contentType: string,
  onLoaded: (loadedBytes: number) => void,
) =>
  new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) onLoaded(event.loaded);
    });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') ?? '');
      } else {
        reject(makeS3UploadError(`S3 part upload failed with status ${xhr.status}`, xhr.status));
      }
    };
    xhr.onerror = () => reject(makeS3UploadError('Network error during S3 part upload'));
    xhr.send(blob);
  });

/**
 * Owns AI scan walkthrough upload + job polling for one scan.
 * Upload and VideoGroupImageCaptureJob enqueue happen on batch submit; the row polls independently.
 *
 * Concurrent uploads enqueue independent ScanVideoAudioExtractJobs for the same
 * scanId. The worker does not serialize per scan — overlapping jobs may race
 * on scan deltas (last-write-wins). See kai-wiki/decisionlog/2026-08-20-047-*.
 */
export const useAIScanVideoUpload = (scanId: string, enabled = true) => {
  const kaiProApiClient = useKaiProApiClient();
  const { credentials } = useAuth();

  const [batchPhase, setBatchPhase] = useState<BatchPhase>('idle');
  // Non-terminal error surfaced when analysis kickoff fails; retryAnalysis clears it on success.
  const [analysisError, setAnalysisError] = useState<string | undefined>(undefined);
  const [videoProgressById, setVideoProgressById] = useState<
    ReadonlyMap<string, ApiScanVideoProgress>
  >(() => new Map());

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingVideoIdsRef = useRef<Set<string>>(new Set());
  // Consecutive failed reads per video (thrown getOperation / missing id / non-analysis record).
  // Reset to 0 on any successful read; the poll gives up on a video only after this reaches the cap.
  const pollFailureCountByVideoIdRef = useRef<Map<string, number>>(new Map());
  const operationIdByVideoIdRef = useRef<Map<string, string>>(new Map());
  const fileIdByVideoIdRef = useRef<Map<string, string>>(new Map());
  // Uploaded videos whose analysis kickoff has not yet succeeded.
  const uploadedVideosRef = useRef<{ videoId: string; fileId: string }[]>([]);
  const handlersRef = useRef<BatchUploadHandlers>({});

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const pollAllActiveVideos = useCallback(async () => {
    const videoIds = [...pollingVideoIdsRef.current];
    if (!videoIds.length) {
      stopPolling();
      return;
    }

    const results = await Promise.all(
      videoIds.map(async videoId => {
        try {
          const operationId = operationIdByVideoIdRef.current.get(videoId);
          if (!operationId) return null;
          const operation = await kaiProApiClient.getOperation(operationId);
          if (!isScanVideoAnalysisOperation(operation)) return null;
          return {
            videoId,
            polled: toApiScanVideoProgressFromOperation(videoId, operation),
          };
        } catch {
          return null;
        }
      }),
    );

    const polledByVideoId = new Map<string, ApiScanVideoProgress>();
    for (const result of results) {
      if (result) polledByVideoId.set(result.videoId, result.polled);
    }

    // The server declares a job done: on a terminal read we drop the video from the poll set (and
    // forget its correlation record). A successful non-terminal read resets that video's failure
    // count so a healthy long job is polled indefinitely. The one give-up that is NOT wall-clock:
    // after SCAN_VIDEO_JOB_POLL_MAX_CONSECUTIVE_FAILURES successive unreadable reads (thrown request,
    // pruned/404, wrong type) we release the video with a "lost contact" failure and log it, so the
    // error path can no longer pin the interval and batchPhase forever (the #2567 regression).
    const connectionLostByVideoId = new Map<string, ApiScanVideoProgress>();
    for (const videoId of videoIds) {
      if (!pollingVideoIdsRef.current.has(videoId)) continue;

      const polled = polledByVideoId.get(videoId);

      if (polled) {
        pollFailureCountByVideoIdRef.current.delete(videoId);
        if (polled.lifecycleStatus === 'completed' || polled.lifecycleStatus === 'failed') {
          const terminalOperationId = operationIdByVideoIdRef.current.get(videoId);
          if (terminalOperationId) forgetScanVideoOperationId(scanId, terminalOperationId);
          pollingVideoIdsRef.current.delete(videoId);
          operationIdByVideoIdRef.current.delete(videoId);
        }
        continue;
      }

      const failures = (pollFailureCountByVideoIdRef.current.get(videoId) ?? 0) + 1;
      pollFailureCountByVideoIdRef.current.set(videoId, failures);
      if (failures >= SCAN_VIDEO_JOB_POLL_MAX_CONSECUTIVE_FAILURES) {
        const lostOperationId = operationIdByVideoIdRef.current.get(videoId);
        logError('AI scan video analyze poll gave up after consecutive unreadable reads', undefined, {
          scanId,
          videoId,
          operationId: lostOperationId,
          consecutiveFailures: failures,
        });
        if (lostOperationId) forgetScanVideoOperationId(scanId, lostOperationId);
        pollingVideoIdsRef.current.delete(videoId);
        operationIdByVideoIdRef.current.delete(videoId);
        pollFailureCountByVideoIdRef.current.delete(videoId);
        connectionLostByVideoId.set(videoId, buildScanVideoPollConnectionLostProgress(videoId));
      }
    }

    setVideoProgressById(current => {
      const next = new Map(current);
      for (const [videoId, polled] of polledByVideoId) {
        next.set(videoId, polled);
      }
      for (const [videoId, lost] of connectionLostByVideoId) {
        next.set(videoId, lost);
      }
      return next;
    });

    for (const [videoId, polled] of polledByVideoId) {
      const fileId = fileIdByVideoIdRef.current.get(videoId);
      if (fileId) handlersRef.current.onJobProgress?.(fileId, polled);
    }

    for (const [videoId, lost] of connectionLostByVideoId) {
      const fileId = fileIdByVideoIdRef.current.get(videoId);
      if (fileId) handlersRef.current.onJobProgress?.(fileId, lost);
    }

    if (!pollingVideoIdsRef.current.size) {
      stopPolling();
      setBatchPhase('idle');
    }
  }, [kaiProApiClient, scanId, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    void pollAllActiveVideos();
    pollRef.current = setInterval(() => {
      void pollAllActiveVideos();
    }, SCAN_VIDEO_JOB_POLL_INTERVAL_MS);
  }, [pollAllActiveVideos]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }
    if (pollingVideoIdsRef.current.size > 0 && !pollRef.current) {
      startPolling();
    }
  }, [enabled, startPolling, stopPolling]);

  /**
   * Start VideoGroupImageCaptureJob for every uploaded video in the batch, then poll the operations.
   */
  const startAnalysis = useCallback(async () => {
    const entries = uploadedVideosRef.current;

    if (!entries.length) {
      setAnalysisError(undefined);
      return;
    }

    setAnalysisError(undefined);
    setBatchPhase('processing');

    // Kick off each uploaded video; keep the ones that failed so the operator can retry exactly those.
    // A kickoff failure is an *analysis* failure (the video uploaded fine and is persisted) — it must
    // not flip the row to `bad` or leave the operator stranded with no way back.
    const failedEntries: { videoId: string; fileId: string }[] = [];
    await Promise.all(entries.map(async entry => {
      const { videoId, fileId } = entry;
      try {
        const operation = await kickOffVideoGroupImageCaptureJob(kaiProApiClient, videoId);
        // Correlate this started operation to the scan so only this tab's jobs rehydrate on reopen.
        rememberScanVideoOperationId(scanId, operation.id);
        operationIdByVideoIdRef.current.set(videoId, operation.id);
        fileIdByVideoIdRef.current.set(videoId, fileId);
        pollingVideoIdsRef.current.add(videoId);
        handlersRef.current.onAnalyzeStarted?.(fileId, videoId);
      } catch {
        failedEntries.push(entry);
      }
    }));

    uploadedVideosRef.current = failedEntries;

    if (failedEntries.length) {
      setAnalysisError('Couldn’t start analysis. Try again.');
    }

    if (!pollingVideoIdsRef.current.size) {
      setBatchPhase('idle');
      return;
    }
    if (enabled) startPolling();
  }, [enabled, kaiProApiClient, scanId, startPolling]);

  const registerVideoForPolling = useCallback((videoId: string, fileId: string, operationId?: string) => {
    if (operationId) operationIdByVideoIdRef.current.set(videoId, operationId);
    fileIdByVideoIdRef.current.set(videoId, fileId);
    pollingVideoIdsRef.current.add(videoId);
    setBatchPhase('processing');
    if (enabled) startPolling();
  }, [enabled, startPolling]);

  const registerVideosForPolling = useCallback((entries: readonly { videoId: string; fileId: string }[]) => {
    if (!entries.length) return;
    for (const { videoId, fileId } of entries) {
      fileIdByVideoIdRef.current.set(videoId, fileId);
      pollingVideoIdsRef.current.add(videoId);
    }
    setBatchPhase('processing');
    if (enabled) startPolling();
  }, [enabled, startPolling]);

  const resumeFromServerItems = useCallback((
    items: readonly AIScanVideoActiveWorkSnapshotItem[],
    handlers: BatchUploadHandlers = {},
  ) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };

    const analyzingItems = items.filter(item => item.lifecycleStatus);
    if (!analyzingItems.length) return;

    for (const item of analyzingItems) {
      if (item.operationId) operationIdByVideoIdRef.current.set(item.videoId, item.operationId);
    }

    registerVideosForPolling(
      analyzingItems.map(item => ({ videoId: item.videoId, fileId: item.videoId })),
    );

    setVideoProgressById(current => {
      const next = new Map(current);
      for (const item of analyzingItems) {
        next.set(item.videoId, {
          videoId: item.videoId,
          lifecycleStatus: item.lifecycleStatus!,
          progress: item.progress ?? null,
          error: undefined,
        });
      }
      return next;
    });

    for (const item of analyzingItems) {
      handlersRef.current.onJobProgress?.(item.videoId, {
        videoId: item.videoId,
        lifecycleStatus: item.lifecycleStatus!,
        progress: item.progress ?? null,
        error: undefined,
      });
    }
  }, [registerVideosForPolling]);

  /**
   * The one upload path for every walkthrough, small or large: multipart end to end. Streaming-hash
   * for dedupe, then upload only the missing parts (per-part retry + on-demand re-sign of expired
   * URLs), throttled by the batch-shared limiter. Interruptions resume when the same file is
   * re-submitted — init returns resumeMultipart with the parts already in S3, and only the gap
   * re-uploads. A sub-part-size file is simply a single-part multipart upload.
   */
  const uploadFile = useCallback(
    async (
      item: UploadFileItem,
      limiter: ScanVideoConcurrencyLimiter,
      onProgress?: (pct: number) => void,
    ) => {
      const { file } = item;
      const contentType = resolveVideoContentType(file);
      if (!contentType) {
        throw new Error('Unsupported video format');
      }
      // Client-read metadata persisted on complete, mirroring the single-part path.
      const metadata = {
        ...(item.durationSeconds != null ? { durationSeconds: item.durationSeconds } : {}),
        ...(item.width != null ? { width: item.width } : {}),
        ...(item.height != null ? { height: item.height } : {}),
        ...(item.videoRecordedAt != null ? { videoRecordedAt: item.videoRecordedAt } : {}),
      };
      const contentHash = await computeScanVideoContentHashStreaming(file);
      const init = await kaiProApiClient.initScanVideoMultipartUpload({
        scanId,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        contentHash,
      });

      if (init.skipUpload) {
        onProgress?.(100);
        if (init.action === 'retryAnalysis') {
          await kaiProApiClient.completeScanVideoMultipartUpload({
            videoId: init.videoId,
            objectKey: init.objectKey,
            uploadId: init.uploadId ?? '',
            sizeBytes: file.size,
            parts: [],
            ...metadata,
          });
        }
        return init.videoId;
      }

      const { uploadId } = init;
      if (!uploadId) {
        throw new Error('Multipart uploadId missing for video upload');
      }

      const partSizeBytes = init.partSizeBytes ?? SCAN_VIDEO_MULTIPART_PART_SIZE_BYTES;
      const totalParts = init.totalParts ?? getScanVideoPartCount(file.size, partSizeBytes);
      const alreadyUploaded = init.alreadyUploadedParts;
      const missingParts = getMissingScanVideoPartNumbers(totalParts, alreadyUploaded);

      const urlByPart = new Map<number, string>();
      for (const part of init.parts) urlByPart.set(part.partNumber, part.url);

      // Parts resumed from S3 already count toward progress; only the gap re-uploads.
      const resumedBytes = Math.min(alreadyUploaded.length * partSizeBytes, file.size);
      const loadedByPart = new Map<number, number>();
      const emitProgress = () => {
        let uploaded = resumedBytes;
        for (const loaded of loadedByPart.values()) uploaded += loaded;
        onProgress?.(getScanVideoMultipartUploadPct(uploaded, file.size));
      };
      emitProgress();

      const ensurePartUrl = async (partNumber: number) => {
        const cached = urlByPart.get(partNumber);
        if (cached) return cached;
        const signed = await kaiProApiClient.signScanVideoUploadParts({
          videoId: init.videoId,
          uploadId,
          partNumbers: [partNumber],
        });
        for (const part of signed.parts) urlByPart.set(part.partNumber, part.url);
        const url = urlByPart.get(partNumber);
        if (!url) throw new Error(`Failed to sign part ${partNumber}`);
        return url;
      };

      const uploadOnePart = async (partNumber: number): Promise<ScanVideoUploadedPart> => {
        const blob = sliceScanVideoPart(file, partNumber, partSizeBytes);
        let lastError: unknown;
        for (let attempt = 1; attempt <= SCAN_VIDEO_PART_MAX_ATTEMPTS; attempt++) {
          try {
            const url = await ensurePartUrl(partNumber);
            const eTag = await putPartWithProgress(url, blob, contentType, loaded => {
              loadedByPart.set(partNumber, loaded);
              emitProgress();
            });
            loadedByPart.set(partNumber, blob.size);
            emitProgress();
            return { partNumber, eTag };
          } catch (error) {
            lastError = error;
            loadedByPart.set(partNumber, 0);
            emitProgress();
            // Expired/invalid presign → drop the cached URL so the next attempt re-signs it.
            if (isExpiredPresignError(error)) urlByPart.delete(partNumber);
            console.warn(`AI scan video multipart part ${partNumber} attempt ${attempt} failed`, error, {
              event: SCAN_VIDEO_MULTIPART_UPLOAD_EVENT,
              phase: 'attempt',
              scanId,
              videoId: init.videoId,
              partNumber,
              attempt,
              maxAttempts: SCAN_VIDEO_PART_MAX_ATTEMPTS,
              httpStatus: error instanceof Error ? (error as S3UploadError).httpStatus : undefined,
            });
            if (attempt === SCAN_VIDEO_PART_MAX_ATTEMPTS) break;
            await sleep(SCAN_VIDEO_S3_PUT_RETRY_DELAY_MS);
          }
        }
        throw lastError instanceof Error ? lastError : new Error(`Part ${partNumber} upload failed`);
      };

      const uploadedParts = await Promise.all(
        missingParts.map(partNumber => limiter.run(() => uploadOnePart(partNumber))),
      );

      // On resume we lack ETags for parts already in S3, so send an empty list and let the server
      // reconcile the full set from ListParts. A fresh upload sends its collected ETags (also []
      // when the CORS policy hid the ETag header — the same reconciliation path).
      const completeParts = alreadyUploaded.length > 0 ? [] : partsToComplete(uploadedParts);

      await kaiProApiClient.completeScanVideoMultipartUpload({
        videoId: init.videoId,
        objectKey: init.objectKey,
        uploadId,
        sizeBytes: file.size,
        parts: completeParts,
        ...metadata,
      });
      onProgress?.(100);
      return init.videoId;
    },
    [kaiProApiClient, scanId],
  );

  const submitBatch = useCallback(
    async (items: readonly UploadFileItem[], handlers: BatchUploadHandlers = {}) => {
      if (!credentials.token) {
        items.forEach(item => handlers.onFileUploadFailed?.(item.fileId, 'You must be logged in to upload a video.'));
        return;
      }
      if (!items.length) return;

      handlersRef.current = handlers;
      setBatchPhase('submitting');
      setAnalysisError(undefined);
      uploadedVideosRef.current = [];

      // One limiter for the whole batch caps simultaneous part PUTs across every multipart file.
      const partLimiter = createScanVideoConcurrencyLimiter(SCAN_VIDEO_PART_CONCURRENCY);

      await Promise.all(
        items.map(async item => {
          try {
            const videoId = await uploadFile(item, partLimiter, pct => {
              handlers.onFileUploadProgress?.(item.fileId, pct);
            });
            uploadedVideosRef.current.push({ videoId, fileId: item.fileId });
            handlers.onFileUploadComplete?.(item.fileId, videoId);
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload failed';
            if (isS3UploadError(error)) {
              logError('AI scan video S3 upload failed', error, {
                event: SCAN_VIDEO_MULTIPART_UPLOAD_EVENT,
                phase: 'batch',
                scanId,
                fileId: item.fileId,
                fileName: item.file.name,
                fileSizeBytes: item.file.size,
                contentType: resolveVideoContentType(item.file) ?? item.file.type,
                httpStatus: error.httpStatus,
              });
            }
            handlers.onFileUploadFailed?.(item.fileId, message);
          }
        }),
      );

      if (uploadedVideosRef.current.length) {
        await startAnalysis();
      } else {
        setBatchPhase('idle');
      }
    },
    [credentials.token, scanId, startAnalysis, uploadFile],
  );

  const retryAnalysis = useCallback(async () => {
    await startAnalysis();
  }, [startAnalysis]);

  const reset = useCallback(() => {
    stopPolling();
    pollingVideoIdsRef.current.clear();
    pollFailureCountByVideoIdRef.current.clear();
    operationIdByVideoIdRef.current.clear();
    fileIdByVideoIdRef.current.clear();
    uploadedVideosRef.current = [];
    handlersRef.current = {};
    setBatchPhase('idle');
    setAnalysisError(undefined);
    setVideoProgressById(new Map());
  }, [stopPolling]);

  return {
    batchPhase,
    analysisError,
    videoProgressById,
    submitBatch,
    retryAnalysis,
    reset,
    resumeFromServerItems,
    isUploading: batchPhase === 'submitting',
    isBatchActive: batchPhase !== 'idle' || analysisError != null,
  };
};
